import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { Container, Typography, Grid, Card, CardContent, Button, Box, Stack, Chip, CircularProgress, TextField, Modal, Fade, Backdrop, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { motion } from 'framer-motion';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditableImage from '../../components/ui/EditableImage';
import { useSubscribers } from '../../hooks/useSubscribers';
import SEO from '../../components/seo/SEO';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

const BlogPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language || 'en';

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [modalState, setModalState] = useState({ open: false, type: 'success', message: '' });

  const subscribeMutation = useSubscribers();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !fullName) {
      setModalState({
        open: true,
        type: 'error',
        message: t("blogPage.validation.requiredFields")
      });
      return;
    }
    
    subscribeMutation.mutate({ email, full_name: fullName }, {
      onSuccess: () => {
        setModalState({
          open: true,
          type: 'success',
          message: t("blogPage.modal.messages.success", { name: fullName })
        });
        setEmail('');
        setFullName('');
      },
      onError: (err) => {
        if (err.message?.includes('unique constraint') || err.code === '23505') {
          setModalState({
            open: true,
            type: 'warning',
            message: t("blogPage.modal.messages.alreadySubscribed")
          });
        } else {
          setModalState({
            open: true,
            type: 'error',
            message: err.message || 'Error subscribing'
          });
        }
      }
    });
  };

  const { data: posts, isLoading } = useQuery({

    queryKey: ['blogPosts', currentLang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          *,
          translations:blog_posts_translations(*),
          skills:blog_post_skills(skills(*, translations:skills_translations(*))),
          technologies:blog_post_technologies(technologies(*))
        `)
        .eq('is_published', true)
        .eq('blog_posts_translations.language_code', currentLang)
        .order('display_order', { ascending: true });

      if (error) throw error;
      
      return data.map(post => {
        const translation = post.translations?.[0] || {};
        const mappedSkills = post.skills?.map(s => ({
          id: s.skills.id,
          name: s.skills.translations?.find(t => t.language_code === currentLang)?.name || s.skills.translations?.[0]?.name
        })) || [];
        const mappedTech = post.technologies?.map(t => ({
          id: t.technologies.id,
          name: t.technologies.name
        })) || [];

        return {
          ...post,
          title: translation.title,
          excerpt: translation.excerpt,
          skills: mappedSkills,
          technologies: mappedTech
        };
      });
    }
  });

  return (
    <Box component="main" sx={{ bgcolor: '#0B0F14', minHeight: '100vh', pb: 20 }}>
      <SEO 
        title={t('blog.seo.title', { defaultValue: 'Blog' })}
        description={t('blog.seo.description', { defaultValue: "Articles techniques sur le Data Science, Machine Learning, IA et développement web. Tutoriels et retours d'expérience." })}
        keywords="blog, data science, machine learning, IA, tutoriels, articles techniques"
      />
      {/* Hero Header */}
      <Box sx={{ py: 15, bgcolor: '#111827', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="overline" 
              align="center"
              sx={{ color: '#0FA3A3', fontWeight: 700, letterSpacing: 3, display: 'block', mb: 2 }}
            >
              {t("blogPage.header.overline")}
            </Typography>
            <Typography 
              variant="h1" 
              align="center"
              sx={{ color: 'white', mb: 3, fontFamily: 'Philosopher', fontSize: { xs: '2.5rem', md: '4rem' } }}
            >
              {t("blogPage.header.titlePrefix")}{" "}
              <span style={{ color: '#D4AF37' }}>{t("blogPage.header.titleHighlight")}</span>{" "}
              {t("blogPage.header.titleSuffix")}
            </Typography>
            <Typography 
              variant="h6" 
              align="center"
              sx={{ color: '#9CA3AF', fontWeight: 300, lineHeight: 1.6, maxWidth: '700px', mx: 'auto' }}
            >
              {t("blogPage.header.subtitle")}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 10 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#0FA3A3' }} />
          </Box>
        ) : (
          <Grid container spacing={6}>
            {posts?.map((post, index) => (
              <Grid item xs={12} key={post.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', md: 'row' },
                      bgcolor: '#1F2937', 
                      borderRadius: '32px',
                      overflow: 'hidden',
                      border: '1px solid rgba(255,255,255,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#D4AF37',
                        transform: 'translateY(-8px)',
                        boxShadow: '0 30px 60px rgba(0,0,0,0.4)',
                        '& .blog-img': { transform: 'scale(1.05)' }
                      }
                    }}
                  >
                    <Box sx={{ width: { xs: '100%', md: '45%' }, height: { xs: '300px', md: 'auto' }, overflow: 'hidden' }}>
                      <EditableImage 
                        src={post.cover_image_url}
                        alt={post.title}
                        tableName="blog_posts"
                        recordId={post.id}
                        imageField="cover_image_url"
                        sx={{ width: '100%', height: '100%' }}
                        imgSx={{ 
                          transition: 'transform 0.6s ease',
                          filter: 'brightness(0.8)'
                        }}
                        className="blog-img"
                      />
                    </Box>
                    <CardContent sx={{ p: { xs: 4, md: 6 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ fontSize: '0.9rem', color: '#0FA3A3' }} />
                        <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 600 }}>
                          {new Date(post.published_date || post.created_at).toLocaleDateString(currentLang, { month: 'long', day: 'numeric', year: 'numeric' })}
                        </Typography>
                      </Box>
                      {post.skills?.[0] && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalOfferIcon sx={{ fontSize: '0.9rem', color: '#D4AF37' }} />
                          <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 600 }}>
                            {post.skills[0].name}
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    <Typography 
                      variant="h3" 
                      component="div"
                      sx={{ 
                        color: 'white', 
                        mb: 3, 
                        fontFamily: 'Philosopher',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        '& h1': { fontSize: 'inherit', margin: 0 },
                        '& p': { margin: 0 }
                      }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                        {post.title}
                      </ReactMarkdown>
                    </Typography>

                    <Typography variant="body1" component="div" sx={{ color: '#9CA3AF', mb: 4, lineHeight: 1.8, fontSize: '1.1rem' }}>
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm, remarkBreaks]}
                        components={{
                          strong: ({node, ...props}) => <Box component="strong" sx={{ color: '#D4AF37', fontWeight: 700 }} {...props} />,
                          a: ({node, ...props}) => <Box component="a" sx={{ color: '#0FA3A3', textDecoration: 'underline', '&:hover': { color: '#0D8A8A' } }} {...props} />,
                        }}
                      >
                        {post.excerpt}
                      </ReactMarkdown>
                    </Typography>

                    <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {post.technologies?.map(tech => (
                          <Chip 
                            key={tech.id} 
                            label={tech.name} 
                            size="small" 
                            sx={{ 
                              bgcolor: 'rgba(212, 175, 55, 0.1)', 
                              color: '#D4AF37', 
                              border: '1px solid rgba(212, 175, 55, 0.2)',
                              fontWeight: 600
                            }} 
                          />
                        ))}
                      </Box>
                      <Button 
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate(`/blog/${post.slug}`)}
                        sx={{ 
                          color: 'white', 
                          fontWeight: 700,
                          '&:hover': { bgcolor: 'transparent', color: '#0FA3A3', transform: 'translateX(5px)' },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {t("blogPage.actions.readArticle")}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Newsletter / CTA Section */}
        <Box sx={{ 
          mt: 15, 
          p: { xs: 6, md: 10 }, 
          bgcolor: '#111827', 
          borderRadius: '40px', 
          textAlign: 'center',
          border: '1px solid rgba(255,255,255,0.05)'
        }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 2, fontFamily: 'Philosopher' }}>
            {t("blogPage.newsletter.titlePrefix")}{" "}
            <span style={{ color: '#0FA3A3' }}>{t("blogPage.newsletter.titleHighlight")}</span>
          </Typography>
          <Typography variant="body1" sx={{ color: '#9CA3AF', mb: 6, maxWidth: '500px', mx: 'auto' }}>
            {t("blogPage.newsletter.subtitle")}
          </Typography>
          
          <Box component="form" onSubmit={handleSubscribe} sx={{ maxWidth: '800px', mx: 'auto' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder={t("blogPage.newsletter.namePlaceholder")}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      bgcolor: 'rgba(255,255,255,0.03)',
                      borderRadius: '16px',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                      '&:hover fieldset': { borderColor: '#0FA3A3' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder={t("blogPage.newsletter.emailPlaceholder")}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      bgcolor: 'rgba(255,255,255,0.03)',
                      borderRadius: '16px',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                      '&:hover fieldset': { borderColor: '#0FA3A3' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button 
                  type="submit"
                  variant="contained" 
                  fullWidth
                  disabled={subscribeMutation.isPending}
                  sx={{ 
                    height: '60px',
                    bgcolor: '#D4AF37', 
                    color: '#0B0F14', 
                    fontWeight: 800,
                    borderRadius: '16px',
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#B8962E', transform: 'translateY(-2px)' },
                    transition: 'all 0.3s ease',
                    boxShadow: '0 10px 20px rgba(212, 175, 55, 0.2)'
                  }}
                >
                  {subscribeMutation.isPending ? t("blogPage.newsletter.buttonLoading") : t("blogPage.newsletter.buttonIdle")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Modal
          open={modalState.open}
          onClose={() => setModalState(prev => ({ ...prev, open: false }))}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
            sx: { backdropFilter: 'blur(8px)', bgcolor: 'rgba(0,0,0,0.8)' }
          }}
        >
          <Fade in={modalState.open}>
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 450 },
              bgcolor: '#1F2937',
              borderRadius: '32px',
              boxShadow: modalState.type === 'success' ? '0 0 40px rgba(76, 175, 80, 0.2)' : modalState.type === 'warning' ? '0 0 40px rgba(212, 175, 55, 0.2)' : '0 0 40px rgba(244, 67, 54, 0.2)',
              p: 6,
              textAlign: 'center',
              border: '1px solid',
              borderColor: modalState.type === 'success' ? 'rgba(76, 175, 80, 0.3)' : modalState.type === 'warning' ? 'rgba(212, 175, 55, 0.3)' : 'rgba(244, 67, 54, 0.3)',
              outline: 'none'
            }}>
              <IconButton 
                onClick={() => setModalState(prev => ({ ...prev, open: false }))}
                sx={{ position: 'absolute', top: 16, right: 16, color: 'rgba(255,255,255,0.5)' }}
              >
                <CloseIcon />
              </IconButton>
              
              {modalState.type === 'success' && <CheckCircleOutlineIcon sx={{ fontSize: 80, color: '#4CAF50', mb: 3 }} />}
              {modalState.type === 'warning' && <WarningAmberIcon sx={{ fontSize: 80, color: '#D4AF37', mb: 3 }} />}
              {modalState.type === 'error' && <ErrorOutlineIcon sx={{ fontSize: 80, color: '#F44336', mb: 3 }} />}
              
              <Typography variant="h3" sx={{ color: 'white', mb: 2, fontFamily: 'Philosopher', fontWeight: 800 }}>
                {modalState.type === 'success'
                  ? t("blogPage.modal.titles.success")
                  : modalState.type === 'warning'
                    ? t("blogPage.modal.titles.warning")
                    : t("blogPage.modal.titles.error")}
              </Typography>
              
              <Typography variant="h6" sx={{ color: '#9CA3AF', mb: 4, lineHeight: 1.6 }}>
                {modalState.message}
              </Typography>
              
              <Button 
                variant="contained" 
                fullWidth
                onClick={() => setModalState(prev => ({ ...prev, open: false }))}
                sx={{ 
                  bgcolor: modalState.type === 'success' ? '#4CAF50' : modalState.type === 'warning' ? '#D4AF37' : '#F44336', 
                  color: modalState.type === 'warning' ? '#0B0F14' : 'white', 
                  fontWeight: 800, 
                  py: 2, 
                  borderRadius: '16px',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: modalState.type === 'success' ? '#388E3C' : modalState.type === 'warning' ? '#B8962E' : '#D32F2F' }
                }}
              >
                {t("blogPage.modal.close")}
              </Button>
            </Box>
          </Fade>
        </Modal>
      </Container>
    </Box>
  );
};

export default BlogPage;
